import { defaultBlockAt, extensions, isTextSelection } from '@tiptap/core';
import { EditorState } from '@tiptap/pm/state';
import { liftTarget } from '@tiptap/pm/transform';

// Overrides of Tiptap core commands that crash in this editor. Each override says when it can go;
// remove the extension once none is left.
export const TiptapCommandFixes = extensions.Commands.extend({
  name: 'tiptapCommandFixes',

  addCommands() {
    const splitBlock = this.parent!().splitBlock!;

    return {
      // Tiptap 2.6+ splitBlock checks the split before deleting the selection, so Enter over a selection that spans
      // blocks throws: https://github.com/ueberdosis/tiptap/issues/7734. Dry-run it on a scratch state and refuse the
      // splits that would throw; the real transaction is dispatched even when a command returns false, so it must stay
      // untouched. Remove with the v3 upgrade (fixed in 3.31.4).
      splitBlock: (options) => (props) => {
        const { doc, selection } = props.tr;
        if (isTextSelection(selection) && !selection.empty) {
          const scratch = EditorState.create({ doc, selection });
          try {
            splitBlock(options)({ ...props, tr: scratch.tr, state: scratch, dispatch: () => {} });
          } catch {
            return false;
          }
        }
        return splitBlock(options)(props);
      },

      // Tiptap's clearNodes turns a textblock into the parent's raw default type. The doc rule
      // `(pageBreak? block)+ pageBreak?` makes that a pageBreak after any top-level block, so list toggles and setNode
      // throw "Invalid content for node type pageBreak" (Sentry CONTRACT-BUILDER-2TF; same throw site as
      // https://github.com/ueberdosis/tiptap/issues/3025). Copy of Tiptap 2.27.2's clearNodes that converts to the
      // default textblock instead, as splitBlock does. Still needed on v3 (unchanged upstream); remove once the doc rule or upstream changes.
      clearNodes:
        () =>
        ({ state, tr, dispatch }) => {
          const { selection } = tr;
          const { ranges } = selection;

          if (!dispatch) return true;

          ranges.forEach(({ $from, $to }) => {
            state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
              if (node.type.isText) return;

              const { doc, mapping } = tr;
              const $mappedFrom = doc.resolve(mapping.map(pos));
              const $mappedTo = doc.resolve(mapping.map(pos + node.nodeSize));
              const nodeRange = $mappedFrom.blockRange($mappedTo);
              if (!nodeRange) return;

              const targetLiftDepth = liftTarget(nodeRange);

              if (node.type.isTextblock) {
                tr.setNodeMarkup(
                  nodeRange.start,
                  defaultBlockAt($mappedFrom.parent.contentMatchAt($mappedFrom.index())),
                );
              }

              if (targetLiftDepth || targetLiftDepth === 0) tr.lift(nodeRange, targetLiftDepth);
            });
          });

          return true;
        },
    };
  },
});
